import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileCard } from "@/components/FileCard";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell, AreaChart, Area } from "recharts";
import { FileStack, FolderKanban, Layers, Users2, Building2, ClipboardCheck, ArrowRight, Compass, Loader2, TrendingUp } from "lucide-react";

const STAT_CARDS = [
  { key: "total_files", label: "Source Files", icon: FileStack, to: "/review" },
  { key: "projects", label: "Projects & Topics", icon: FolderKanban, to: "/projects" },
  { key: "categories", label: "Categories", icon: Layers, to: "/categories" },
  { key: "people", label: "People", icon: Users2, to: "/people" },
  { key: "organizations", label: "Organizations", icon: Building2, to: "/organizations" },
];

export default function Dashboard() {
  const { user, isStaff, previewMode } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, [previewMode]);

  if (!data)
    return <div className="flex items-center justify-center h-96 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const s = data.stats;
  const showStaff = isStaff && !previewMode;

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br from-[#111827] to-[#0B0F19] p-8 md:p-10">
        <img src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/80 to-transparent" />
        <div className="relative z-10 max-w-2xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400 mb-3">Executive Dashboard</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight leading-tight">
            Welcome, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-slate-400 mt-3 md:text-lg">
            {showStaff
              ? "A living, AI-organized archive of contributions. Upload material and it is read, understood, categorized and assembled into executive-ready pages automatically."
              : "A curated overview of the projects, initiatives and contributions across the organization. Start with the featured work, then explore in depth."}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/projects" data-testid="start-here-projects" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
              <Compass className="w-4 h-4" /> Start Here — Explore Projects
            </Link>
            {showStaff && (
              <Link to="/upload" data-testid="dash-upload-cta" className="inline-flex items-center gap-2 border border-white/10 hover:bg-white/5 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
                Upload Material <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STAT_CARDS.map((c, i) => {
          const Icon = c.icon;
          return (
            <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`} className="rounded-xl border border-white/5 bg-[#111827] p-5 hover:border-blue-500/30 hover:-translate-y-1 transition-transform transition-colors duration-300 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <Icon className="w-5 h-5 text-blue-400 mb-3" strokeWidth={1.5} />
              <div className="font-display text-3xl font-bold text-white">{s[c.key]}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            </Link>
          );
        })}
      </div>

      {showStaff && (s.needs_review > 0 || s.processing > 0) && (
        <Link to="/review" data-testid="review-alert" className="flex items-center justify-between rounded-xl border border-amber-500/20 bg-amber-500/5 px-5 py-4 hover:bg-amber-500/10 transition-colors">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-5 h-5 text-amber-400" />
            <span className="text-sm text-amber-200">
              {s.needs_review} item(s) need review{s.processing > 0 ? `, ${s.processing} still processing` : ""}. Approve and publish to make them visible to the CEO.
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-400" />
        </Link>
      )}

      {/* Featured projects */}
      {data.featured?.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-white">Featured Projects</h2>
            <Link to="/projects" className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.featured.map((p, i) => (
              <Link key={p.id} to={`/project/${p.id}`} data-testid={`featured-${p.id}`} className="group relative rounded-xl overflow-hidden border border-white/5 h-44 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <img src={p.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-[#070B14]/50 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <div className="text-[10px] uppercase tracking-widest text-blue-300 mb-1">{p.type}</div>
                  <div className="font-display font-semibold text-white leading-snug">{p.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{p.count} file(s)</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 rounded-xl border border-white/5 bg-[#111827] p-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-blue-400" /><h3 className="font-display font-semibold text-white">Contribution Timeline</h3></div>
          {data.timeline?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.timeline}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1F2937", borderRadius: 8, color: "#fff" }} />
                <Area type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-600 py-12 text-center">No activity yet.</p>}
        </div>

        {/* Departments */}
        <div className="rounded-xl border border-white/5 bg-[#111827] p-6">
          <h3 className="font-display font-semibold text-white mb-4">Work by Area</h3>
          {data.departments?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.departments} layout="vertical" margin={{ left: 0 }}>
                <XAxis type="number" hide />
                <Tooltip contentStyle={{ background: "#0B0F19", border: "1px solid #1F2937", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.departments.map((_, i) => <Cell key={i} fill="#3B82F6" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-600 py-12 text-center">No departments yet.</p>}
          <div className="space-y-1.5 mt-2">
            {data.departments?.slice(0, 5).map((d) => (
              <Link key={d.id} to={`/category/${d.id}`} className="flex justify-between text-sm text-slate-400 hover:text-white transition-colors">
                <span className="truncate">{d.name}</span><span className="text-slate-600">{d.count}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Themes */}
      {data.themes?.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold text-white mb-4">Key Themes & Expertise</h2>
          <div className="flex flex-wrap gap-2">
            {data.themes.map((t) => (
              <Link key={t.tag} to={`/search?q=${encodeURIComponent(t.tag)}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#111827] px-4 py-2 text-sm text-slate-300 hover:border-blue-500/30 hover:text-white transition-colors">
                {t.tag} <span className="text-blue-400 font-mono text-xs">{t.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Recent */}
      {data.recent?.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold text-white mb-4">Recently Added</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.recent.map((f, i) => <FileCard key={f.id} f={f} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
